<template>
  <Dropdown
    class-name="session-deliverables"
    :title="t('session.deliverables.title')"
    :default-open="true"
    :is-loaded="!isPendingRef && !isDeliverablesPendingRef"
  >
    <template v-if="sessionRef && sessionRef.deliverables.length > 0">
      <!-- check if there are deliverables with a rating -->
      <p
        class="deliverables-rating-average"
        v-if="
          sessionRef.deliverables.length > 0 &&
          sessionRef.deliverables.some((deliverable) => typeof deliverable.rating === 'number')
        "
      >
        <span>{{ t('session.deliverables.averageOfGrades') }}</span>
        <span>
          <!-- take only the deliverables that have a rating (filter) and calculate the average (reduce) -->
          {{
            Math.round(
              (sessionRef?.deliverables
                .filter((deliverable) => typeof deliverable.rating === 'number')
                .reduce((acc, deliverable) => acc + (deliverable.rating ?? 0), 0) /
                sessionRef?.deliverables.filter((deliverable) => typeof deliverable.rating === 'number').length) *
                10,
            ) / 10
          }}/20
        </span>
      </p>
      <div class="deliverables-listing">
        <div v-for="mentee in menteesRef" :key="mentee?.id" class="deliverable-card no-actions">
          <div class="deliverable-card-top-row">
            <UserInfo v-if="mentee" :user="mentee" :sub-info="' '" />
            <p class="deliverable-rating">
              <span
                v-if="
                  typeof returnDeliverableByUserId({ deliverables: deliverablesRef, menteeId: mentee?.id })?.rating ===
                  'number'
                "
                >{{
                  returnDeliverableByUserId({
                    deliverables: deliverablesRef,
                    menteeId: mentee?.id,
                  })?.rating
                }}/20</span
              >
              <span v-else>{{ t('session.deliverables.notGraded') }}</span>
            </p>
          </div>
          <div class="deliverables-list">
            <template
              v-if="
                returnDeliverableByUserId({
                  deliverables: deliverablesRef,
                  menteeId: mentee?.id,
                })?.folder?.files
              "
            >
              <Deliverable
                v-for="file in returnDeliverableByUserId({
                  deliverables: deliverablesRef,
                  menteeId: mentee?.id,
                })?.folder?.files"
                :key="file?.id"
                :session-id="sessionIdRef"
                :user-id="mentee?.id"
                :file="file"
              />
            </template>
            <template v-else>
              <EmptyContent :text="t('session.deliverables.noDeliverable')" />
            </template>
          </div>
        </div>
      </div>
      <div class="c-btns-container c-btn-center">
        <CustomButton
          v-if="groupRolesRef?.includes(RoleEnumTypeV2.MENTOR)"
          usage="button"
          color="primary"
          :text="t('session.deliverables.modifyRating')"
          @emit:click="onClickModifyRating"
        >
          <template #icon>
            <EditIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </template>
    <EmptyContent v-else :text="t('session.deliverables.noGradeAndDeliverable')" />
  </Dropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { RoleEnumTypeV2 } from '@/features/auth/types'
import { getMentees } from '@/features/groups/utils'
import Deliverable from '@/features/sessions/components/deliverable/deliverable.vue'
import useDeliverables from '@/features/sessions/hooks/use-deliverables.hook'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { returnDeliverableByUserId } from '@/features/sessions/utils'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import Dropdown from '@/features/ui/components/dropdown/dropdown.vue'
import EmptyContent from '@/features/ui/components/empty-content/empty-content.vue'
import EditIcon from '@/features/ui/components/icons/edit-icon.vue'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import type { ReviewSessionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import UserInfo from '@/features/users/components/user-info/user-info.vue'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)

const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)

const { sessionRef, isPendingRef } = useSession(sessionIdRef)
const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId ?? null)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const menteesRef = computed(() => sessionRef.value?.users.filter(getMentees) ?? [])

const { deliverablesRef, isPendingRef: isDeliverablesPendingRef } = useDeliverables(sessionIdRef)

const onClickModifyRating = () => {
  modalsStore.toggleModal('review-session', true)
  modalsStore.toggleModal('session-details', false)

  modalsStore.setModalStep<ReviewSessionModalType>('review-session', 'deliverables-rating')
  modalsStore.updateModalAttachedData<ReviewSessionModalType>('review-session', {
    sessionId: sessionIdRef.value!,
  })
}
</script>

<style lang="scss">
@import './session-deliverables.scss';
</style>
